import type { AxiosError } from 'axios';
import type { Dispatch } from 'react';
import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';

import { ROUTES } from '@constants/routes';
import type { PatientOrderingEligibilityRequest } from '@customTypes/test-ordering';
import { getEligibility } from '@store/actions/test-ordering';
import { isObject } from '@utils/common';
import { isMinorDOB } from '@utils/dates';
import { touchFieldsWithErrors } from '@utils/forms';

import type {
  OrderingEligibilityFormikHelpers,
  OrderingEligibilityRawFormValues,
} from '../OrderCheckEligibility/OrderCheckEligibility.validation';
import { OrderActions, type OrderActionType } from '../types/Order.actions';
import { collectEligibilityFieldErrors } from '../utils/validationErrors';
import { useOrderContext } from './useOrderContext';

interface UseEligibilitySubmissionProps {
  dispatch: Dispatch<OrderActionType>;
}

interface UseEligibilitySubmissionReturn {
  submitEligibilityCheck: (
    values: OrderingEligibilityRawFormValues,
    formikHelpers: OrderingEligibilityFormikHelpers,
  ) => Promise<void>;
}

interface BuildEligibilityPayloadParams {
  values: OrderingEligibilityRawFormValues;
  isMinorSubject: boolean;
  campaignCode: string | null;
}

export const buildEligibilityPayload = (params: BuildEligibilityPayloadParams): PatientOrderingEligibilityRequest => {
  const { campaignCode, isMinorSubject, values } = params;

  const payload: PatientOrderingEligibilityRequest = {
    campaign_code: campaignCode,
    promo_code: values.promoCode || null,
    subject: {
      first_name: values.firstName,
      last_name: values.lastName,
      dob: values.dob,
      sex: values.sexAtBirth,
      email: values.email || null,
      phone: values.phone || null,
    },
  };

  if (isMinorSubject) {
    const guardian = values.guardian;

    payload.guardian = {
      first_name: guardian.firstName,
      last_name: guardian.lastName,
      dob: guardian.dob,
      relationship: guardian.relationship,
      email: guardian.email,
      phone: guardian.phone,
    };
  }

  return payload;
};

/**
 * Hook that handles the eligibility check submission flow:
 * `getEligibility`, dispatch/navigation on success, and Formik `setErrors` for LIMS field validation
 * (`errors` payload). Non-field failures navigate to the not-eligible route.
 */
export function useEligibilitySubmission({ dispatch }: UseEligibilitySubmissionProps): UseEligibilitySubmissionReturn {
  const navigate = useNavigate();
  const { partnerConfig, partner } = useOrderContext();
  const campaignCode = partnerConfig?.campaignCode || null;
  const allowsMinorWithGuardian = !!partnerConfig?.allowsMinorWithGuardian;

  const submitInFlightRef = useRef(false);

  const buildRoute = useCallback(
    (path: string) => {
      if (partner) {
        return `/${ROUTES.PARTNERSHIP}/${partner}/${path}`;
      }
      return `${ROUTES.ORDER}/${path}`;
    },
    [partner],
  );

  const submitEligibilityCheck = useCallback(
    async (values: OrderingEligibilityRawFormValues, formikHelpers: OrderingEligibilityFormikHelpers) => {
      if (values.boneMarrowTransplant === 'YES') {
        // UI disables Continue; guard remains so a stray submit never calls the API.
        return;
      }

      if (submitInFlightRef.current) {
        return;
      }
      submitInFlightRef.current = true;

      const isMinorSubject = allowsMinorWithGuardian && isMinorDOB(values.dob);

      const payload = buildEligibilityPayload({
        campaignCode,
        isMinorSubject,
        values,
      });

      try {
        const response = await getEligibility(payload);

        if (!response || response.is_eligible !== true) {
          navigate(buildRoute(ROUTES.ORDER_NOT_ELIGIBLE));
          return;
        }

        dispatch({
          type: OrderActions.ELIGIBILITY_UPDATED,
          value: response,
        });

        dispatch({
          type: OrderActions.SET_SUBJECT,
          value: {
            name: {
              first: values.firstName,
              last: values.lastName,
            },
            contact_info: [
              {
                email: values.email || '',
                phone: values.phone || '',
                address: response.campaign?.member?.address || undefined,
              },
            ],
            demographics: {
              dob: values.dob,
              sex: values.sexAtBirth,
            },
          },
        });

        if (isMinorSubject) {
          dispatch({
            type: OrderActions.SET_GUARDIAN,
            value: {
              first_name: values.guardian.firstName,
              last_name: values.guardian.lastName,
              dob: values.guardian.dob,
              email: values.guardian.email,
              phone: values.guardian.phone,
              relationship: values.guardian.relationship,
            },
          });
        } else {
          dispatch({
            type: OrderActions.SET_GUARDIAN,
            value: null,
          });
        }

        navigate(buildRoute(ROUTES.ORDER_WIZARD));
      } catch (err) {
        const data = (err as AxiosError<unknown>)?.response?.data;
        const errorsPayload = isObject(data) && 'errors' in data ? data.errors : undefined;

        const fieldErrors = collectEligibilityFieldErrors(errorsPayload, { isMinorSubject });

        if (Object.keys(fieldErrors).length > 0) {
          formikHelpers.setErrors(fieldErrors);

          touchFieldsWithErrors(fieldErrors, formikHelpers);
          return;
        }

        navigate(buildRoute(ROUTES.ORDER_NOT_ELIGIBLE));
      } finally {
        formikHelpers.setSubmitting(false);
        submitInFlightRef.current = false;
      }
    },
    [allowsMinorWithGuardian, buildRoute, campaignCode, dispatch, navigate],
  );

  return { submitEligibilityCheck };
}
